import { useCallback, useId, useMemo } from "react";

import { NodeTransformationType } from "@doitintl/cmp-models";
import { FormControl, FormHelperText, InputLabel, MenuItem, Select, Stack } from "@mui/material";
import { useFormikContext } from "formik";
import upperFirst from "lodash/upperFirst";
import words from "lodash/words";
import * as yup from "yup";

import { cloudflowTexts } from "../../../../../../assets/texts";
import { useFieldCommonProps } from "../../../ApiActionParametersForm/useFieldCommonProps";
import { FieldSectionHeader } from "../../../Common/FieldSectionHeader";
import { ConcatenationForm } from "./ConcatenationForm";
import { ExtractTransformationForm } from "./ExtractTransformationForm";
import { FirstItemTransformationForm } from "./FirstItemTransformationForm";
import { JoinTransformationForm } from "./JoinTransformationForm";
import { LastItemTransformationForm } from "./LastItemTransformationForm";
import { NumericTransformationForm } from "./NumericTransformationForm";
import { StringTransformationForm } from "./StringTransformationForm";
import { type TransformationNodeFormValues } from "./TransformationNodeForm";

const actionLabel = "Transform action";

export const baseTransformationSchema = yup.object({
  type: yup.string().nullable().default(null).required().label(actionLabel),
});

export const TransformationActionPicker = () => {
  const { getFieldProps, setFieldValue } = useFormikContext<TransformationNodeFormValues>();

  const actionPickerLabelId = useId();
  const actionProps = getFieldProps<NodeTransformationType>("type");
  const actionCommonProps = useFieldCommonProps(actionProps, actionLabel, true);

  const transformationActionNode = useMemo(() => {
    switch (actionProps.value) {
      case NodeTransformationType.EXTRACT:
        return <ExtractTransformationForm />;
      case NodeTransformationType.FIRST_ITEM:
        return <FirstItemTransformationForm />;
      case NodeTransformationType.LAST_ITEM:
        return <LastItemTransformationForm />;
      case NodeTransformationType.CONCATENATION:
        return <ConcatenationForm />;
      case NodeTransformationType.UPPER_CASE:
        return <StringTransformationForm type={NodeTransformationType.UPPER_CASE} />;
      case NodeTransformationType.LOWER_CASE:
        return <StringTransformationForm type={NodeTransformationType.LOWER_CASE} />;
      case NodeTransformationType.JOIN:
        return <JoinTransformationForm />;
      case NodeTransformationType.AVERAGE:
        return (
          <NumericTransformationForm
            key="average"
            type={NodeTransformationType.AVERAGE}
            title={cloudflowTexts.AVERAGE_TRANSFORMATION_TITLE}
            subtitle={cloudflowTexts.AVERAGE_TRANSFORMATION_SUBTITLE}
          />
        );
      case NodeTransformationType.SUM:
        return (
          <NumericTransformationForm
            key="sum"
            type={NodeTransformationType.SUM}
            title={cloudflowTexts.SUM_TRANSFORMATION_TITLE}
            subtitle={cloudflowTexts.SUM_TRANSFORMATION_SUBTITLE}
          />
        );
      default:
        return null;
    }
  }, [actionProps.value]);

  const onChangeCallback = useCallback(
    (e) => {
      setFieldValue("transformation", null);
      actionCommonProps.onChange(e);
    },
    [actionCommonProps, setFieldValue]
  );

  return (
    <Stack
      spacing={2}
      sx={{
        mb: 2,
      }}
    >
      <FieldSectionHeader
        title={cloudflowTexts.DEFINE_TRANSFORM_ACTION}
        subtitle={cloudflowTexts.ADD_TRANSFORM_ACTION}
      />
      <FormControl fullWidth required error={actionCommonProps.error}>
        <InputLabel size="small" id={actionPickerLabelId}>
          {actionCommonProps.label}
        </InputLabel>
        <Select labelId={actionPickerLabelId} size="small" {...actionCommonProps} onChange={onChangeCallback}>
          {Object.values(NodeTransformationType).map((type) => (
            <MenuItem key={type} value={type}>
              {upperFirst(words(type).join(" "))}
            </MenuItem>
          ))}
        </Select>
        {actionCommonProps.error && <FormHelperText>{actionCommonProps.helperText}</FormHelperText>}
      </FormControl>
      {transformationActionNode}
    </Stack>
  );
};
